/**
 * One person's evening, as a sentence (D-380).
 *
 * *"4&ndash;6:30pm, 1 hr free, 7:30&ndash;8pm"* &mdash; the spans they are
 * busy, with whatever is left over named. This is the line on a Home row, and
 * it is what replaced putting a timeline on Home: four people with four shapes
 * of evening, from one rule, with no columns to run out of.
 */

/** A session as this cares about it: when it starts and when it ends. */
export type Span = { startTime: string | null; endTime: string | null }

/**
 * A busy stretch, or the hole between two of them.
 *
 * **Minutes, not text.** A part that arrived pre-formatted could not be
 * rendered any other way, and the same span is wanted differently on a row and
 * in an expanded class. `formatSpan` is the one that writes it down.
 */
export type DayPart =
  | { kind: 'busy'; from: number; to: number }
  | { kind: 'gap'; minutes: number }

const minutes = (time: string) => {
  const [h, m] = time.split(':')
  return Number(h) * 60 + Number(m)
}

/**
 * Merges back-to-back and overlapping sessions into the stretches they cover.
 *
 * **Back-to-back is not a gap and not a clash.** Time ranges here are half-open
 * (D-81) — a class ending at 5:45 and one starting at 5:45 are continuous —
 * and the studio runs almost entirely back-to-back, so a rule that reported a
 * gap between every adjacent pair would produce a sentence nobody reads.
 *
 * **Overlaps merge rather than being reported.** Two things at 7:30 in
 * different rooms is real data — a solo that runs every other week against a
 * weekly technique class — and the row is answering *when are you busy*, not
 * *is this a conflict*. The clash belongs on the expanded class, where there is
 * room to say why.
 */
export function dayShape(sessions: Span[]): DayPart[] {
  const spans = sessions
    .filter((s): s is { startTime: string; endTime: string } =>
      Boolean(s.startTime) && Boolean(s.endTime))
    .map((s) => ({ from: minutes(s.startTime), to: minutes(s.endTime) }))
    .sort((a, b) => a.from - b.from || a.to - b.to)

  if (spans.length === 0) return []

  const merged: { from: number; to: number }[] = []
  for (const span of spans) {
    const last = merged[merged.length - 1]
    // `<=` rather than `<`: touching is continuous, per the half-open rule.
    if (last && span.from <= last.to) last.to = Math.max(last.to, span.to)
    else merged.push({ ...span })
  }

  const parts: DayPart[] = []
  merged.forEach((span, i) => {
    if (i > 0) parts.push({ kind: 'gap', minutes: span.from - merged[i - 1].to })
    parts.push({ kind: 'busy', from: span.from, to: span.to })
  })

  return parts
}

/** Minutes since midnight as the studio writes a time: 16:30 is "4:30". */
function clock(total: number): string {
  const h = Math.floor(total / 60)
  const m = total % 60
  const hour = h % 12 === 0 ? 12 : h % 12
  return m === 0 ? `${hour}` : `${hour}:${String(m).padStart(2, '0')}`
}

const half = (total: number) => (Math.floor(total / 60) < 12 ? 'am' : 'pm')

/**
 * A busy stretch, written out: "4&ndash;6:30pm", "10am&ndash;9pm".
 *
 * **The half of the day is not optional here**, which the first version of this
 * got wrong. Every other 12-hour formatter in the app pairs with a meridiem;
 * this one dropped it, and one real teacher-day &mdash; Maddie Thompson's
 * Tuesday, ten classes &mdash; runs from ten in the morning to nine at night
 * and would have said so nowhere.
 *
 * **Stated once when both ends share it**, because "4pm&ndash;6:30pm" is how a
 * form writes a time and "4&ndash;6:30pm" is how a person does. It is repeated
 * exactly when it changes, which is the case that carries the information.
 */
export function formatSpan(from: number, to: number): string {
  const same = half(from) === half(to)
  return same
    ? `${clock(from)}–${clock(to)}${half(to)}`
    : `${clock(from)}${half(from)}–${clock(to)}${half(to)}`
}

/** One time on its own: "8:15pm". */
export function formatTime(at: number): string {
  return `${clock(at)}${half(at)}`
}

/**
 * How long a hole is, in words a parent would use.
 *
 * Halves rather than decimals — "1½ hr" is how long a wait feels; "1.5 hours"
 * is how a computer describes one.
 */
export function gapLabel(mins: number): string {
  if (mins < 60) return `${mins} min`

  const hours = mins / 60
  if (Number.isInteger(hours)) return `${hours} hr`
  if (mins % 60 === 30) return `${Math.floor(hours)}½ hr`

  return `${Math.floor(hours)} hr ${mins % 60} min`
}

/** Whose evening a row is about. */
export type Subject = 'me' | 'dancer'

/**
 * What to call the hole, which depends on whose evening it is.
 *
 * An hour with nothing to teach is *hers*; an hour between her daughter's
 * classes is a child waiting in the building. Same hole, opposite meanings, and
 * the row would be quietly wrong with one word for both.
 *
 * **Named rather than a boolean.** `gapWord(true)` is unreadable at a call
 * site, and the first person to write `gapWord(isChild)` would invert it with
 * nothing to catch them: both values are valid words, so no test would fail.
 */
export const gapWord = (subject: Subject) => (subject === 'me' ? 'free' : 'gap')
